import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import Joi from 'joi';
import isEqual from 'lodash/isEqual';

import { getShareSubcategoryIdFromIRI, validateSchema } from 'utils/helpers';
import { EURO_SYMBOL } from 'utils/constants';
import { texts } from 'utils/languages/fr';

import { ActionButton, CustomTextField, ErrorMessage, Loader, NativeDatePicker } from 'components';
import { Box, Table, TableBody, TableCell, TableContainer, TableRow } from '@mui/material';

const schema = {
    vl: Joi.number().precision(2).required(),
};

const CapitalAccountForm = ({
    activationDates,
    shareSubcategories,
    tableHead,
    dataLoading,
    createdList,
    completedList,
    onAdd,
    onUpdate,
    loading,
    vl,
}) => {
    const latestActivationDate = useMemo(
        () =>
            activationDates?.length > 0
                ? moment.max(...activationDates.map(ad => moment(ad)))
                : null,
        [activationDates]
    );
    const minDate = useMemo(() => {
        if (vl) {
            // evergreen funds
            return latestActivationDate
                ? latestActivationDate.clone().add(1, 'month').startOf('month')
                : null;
        }

        // non-evergreen funds
        if (latestActivationDate) {
            return latestActivationDate.clone().add(1, 'day');
        } else {
            return moment().subtract(6, 'months');
        }
    }, [vl, latestActivationDate]);
    const maxDate = useMemo(() => {
        // non-evergreen funds
        if (!vl) return null;

        // evergreen funds
        return latestActivationDate
            ? latestActivationDate.clone().add(1, 'month').endOf('month')
            : null;
    }, [vl, latestActivationDate]);

    const defaultFormValue = useMemo(() => {
        if (shareSubcategories.length) {
            let formObj = {};
            shareSubcategories.forEach(sc => {
                const nav = createdList.find(
                    ca => getShareSubcategoryIdFromIRI(ca.shareSubcategory) === sc.id
                );
                formObj[sc.id] = {
                    ...nav,
                    vl: nav?.vl >= 0 ? nav?.vl : '',
                    tri: nav?.tri || '',
                    dpi: nav?.dpi >= 0 ? nav?.dpi : '',
                    rvpi: nav?.rvpi >= 0 ? nav?.rvpi : '',
                    tvpi: nav?.tvpi >= 0 ? nav?.tvpi : '',
                    shareSubcategory: `/api/share_subcategories/${sc.id}`,
                };
            });
            return formObj;
        }
        return {};
    }, [createdList, shareSubcategories]);

    const defaultDateValue = useMemo(() => {
        if (createdList.length) return moment(createdList[0].date);
        if (completedList.length) return moment(completedList[0].date);
        return minDate;
    }, [createdList, completedList, minDate]);

    const [form, setForm] = useState({});
    const [formDate, setFormDate] = useState(null);
    const [formErrors, setFormErrors] = useState({});

    const changesMade = useMemo(
        () => !defaultDateValue?.isSame(formDate, 'day') || !isEqual(defaultFormValue, form),
        [form, formDate, defaultDateValue, defaultFormValue]
    );

    const handleChange = useCallback(
        (key, sc) => e => {
            const { value } = e.target;
            const { valid } = validateSchema(schema[key], +value, { convert: false });
            setFormErrors(prevState => {
                const newState = { ...prevState };
                if (valid && !!newState[sc]) delete newState[sc][key];
                if (newState[sc] && !Object.keys(newState[sc]).length) delete newState[sc];
                return newState;
            });
            setForm(prevState => ({
                ...prevState,
                [sc]: {
                    ...prevState[sc],
                    [key]: value !== '' && key !== 'tri' ? +value : value,
                },
            }));
        },
        []
    );

    const handleDateChange = useCallback(newDate => {
        setFormErrors({});
        setFormDate(newDate);
    }, []);

    const handleSaveData = useCallback(() => {
        const errors = {};
        if (!formDate) errors.date = texts.SAISIR_LE_CHAMP;
        Object.keys(form).forEach(key => {
            const { valid, newErrors } = validateSchema(schema, form[key], { convert: false });
            if (!valid) {
                errors[key] = newErrors;
            }
        });
        if (!Object.keys(errors).length) {
            setFormErrors({});
            if (createdList.length) {
                onUpdate({
                    form,
                    formDate: formDate.utcOffset(0).startOf('day'),
                });
            } else {
                onAdd({
                    form,
                    formDate: formDate.utcOffset(0).startOf('day'),
                });
            }
        } else {
            setFormErrors(errors);
        }
    }, [formDate, form, createdList, onUpdate, onAdd]);

    useEffect(() => {
        if (dataLoading) return;
        setForm(prevState => {
            const formHasIds = Object.values(prevState).every(v => !!v.id);
            if (
                (!Object.keys(prevState).length && Object.keys(defaultFormValue).length) ||
                (!formHasIds && createdList.length) ||
                (!createdList.length && formHasIds)
            ) {
                return defaultFormValue;
            }
            return prevState;
        });
    }, [defaultFormValue, createdList, dataLoading]);

    useEffect(() => {
        setFormDate(defaultDateValue);
    }, [defaultDateValue]);

    useEffect(() => {
        if (!createdList.length && minDate) setFormDate(minDate);
    }, [createdList, minDate]);

    return (
        <React.Fragment>
            <TableContainer>
                <Table>
                    {tableHead}
                    <TableBody>
                        {!!Object.keys(form).length &&
                            shareSubcategories.map((sc, i) => (
                                <TableRow key={sc.id}>
                                    <TableCell>{sc.name}</TableCell>
                                    <TableCell>
                                        <CustomTextField
                                            formatNumber
                                            placeholder={`VL (${EURO_SYMBOL})`}
                                            error={formErrors[sc.id]?.vl}
                                            formControlProps={{ sx: { mb: 0 } }}
                                            showError={false}
                                            value={form[sc.id]?.vl}
                                            onChange={handleChange('vl', sc.id)}
                                        />
                                    </TableCell>
                                    {i === 0 && (
                                        <TableCell sx={{ textAlign: 'center' }} rowSpan={7}>
                                            <NativeDatePicker
                                                sx={{ mb: 0 }}
                                                minDate={minDate}
                                                maxDate={maxDate}
                                                disableFuture={false}
                                                handleDateChange={handleDateChange}
                                                error={!!formErrors.date}
                                                value={formDate}
                                                InputAdornmentProps={{ sx: { margin: 0 } }}
                                            />
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                    </TableBody>
                </Table>
            </TableContainer>
            {!!Object.keys(formErrors).length && (
                <React.Fragment>
                    <ErrorMessage message={texts.FILL_ALL_DATA} align="center" />
                    <ErrorMessage message={texts.DECIMALS_ERROR} align="center" />
                </React.Fragment>
            )}
            <Box textAlign="center" paddingTop={4}>
                <Loader loading={loading} marginTop={0}>
                    <ActionButton
                        disabled={!changesMade}
                        onClick={handleSaveData}
                        variant="contained"
                    >
                        {texts.SAVE}
                    </ActionButton>
                </Loader>
            </Box>
        </React.Fragment>
    );
};

CapitalAccountForm.propTypes = {
    activationDates: PropTypes.array.isRequired,
    shareSubcategories: PropTypes.array.isRequired,
    tableHead: PropTypes.object.isRequired,
    dataLoading: PropTypes.bool.isRequired,
    createdList: PropTypes.array.isRequired,
    completedList: PropTypes.array.isRequired,
    loading: PropTypes.bool,
    vl: PropTypes.bool,
    onAdd: PropTypes.func.isRequired,
    onUpdate: PropTypes.func.isRequired,
};

export default CapitalAccountForm;
